/**
 * PublicMemberPage — /m/:token (V4 Members area). PUBLIC, no chrome.
 * The token IS the capability: valid → lesson list + reader; unknown or
 * revoked → paywall ("This content is for members"). Fail-closed by design —
 * the API returns 404 for anything it can't verify, and we never distinguish
 * "wrong token" from "revoked" for the visitor.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, FileText } from 'lucide-react';
import { BrandLogo } from '@/components/brand/BrandLogo';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.buzzbitx.com';

interface LessonPage {
  id: string;
  parentId: string | null;
  title: string;
  position: number;
  content: string;
}

export default function PublicMemberPage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const [pages, setPages] = useState<LessonPage[] | null>(null);
  const [denied, setDenied] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/member-pages/public/${token}`);
        if (!res.ok) { setDenied(true); return; }
        const json = await res.json();
        const list = (json?.data?.pages ?? []) as LessonPage[];
        setPages(list);
        setSelectedId(list[0]?.id ?? null);
      } catch {
        setDenied(true);
      }
    })();
  }, [token]);

  // Roots first, children indented under their parent (MembersPanel ordering).
  const ordered = useMemo(() => {
    const list = pages ?? [];
    const roots = list.filter((p) => p.parentId === null || !list.some((q) => q.id === p.parentId));
    const out: Array<LessonPage & { depth: number }> = [];
    const walk = (parent: LessonPage, depth: number): void => {
      for (const child of list.filter((p) => p.parentId === parent.id)) {
        out.push({ ...child, depth });
        walk(child, Math.min(depth + 1, 3));
      }
    };
    for (const r of roots) { out.push({ ...r, depth: 0 }); walk(r, 1); }
    return out;
  }, [pages]);

  const current = useMemo(
    () => (pages ?? []).find((p) => p.id === selectedId) ?? null,
    [pages, selectedId],
  );

  if (denied) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 text-center">
          <Lock className="mx-auto h-8 w-8 text-gray-300" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">This content is for members</h1>
          <p className="mt-2 text-sm text-gray-500">
            Your link is invalid or no longer active. If you recently purchased, check your email for the newest members link — or contact the business that sent it.
          </p>
          <div className="mt-8 flex justify-center">
            <BrandLogo size="sm" />
          </div>
        </div>
      </div>
    );
  }

  if (pages === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col md:flex-row">
        {/* Lesson list */}
        <div className="w-full shrink-0 border-b border-gray-200 bg-white p-4 md:min-h-screen md:w-64 md:border-b-0 md:border-r">
          <p className="px-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Members area</p>
          {ordered.length === 0 ? (
            <p className="mt-3 px-2 text-sm text-gray-400">No lessons published yet — check back soon.</p>
          ) : (
            <ul className="mt-3 space-y-0.5">
              {ordered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(p.id)}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                      selectedId === p.id ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    style={{ paddingLeft: 8 + p.depth * 14 }}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                    <span className="truncate">{p.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Reader */}
        <div className="min-w-0 flex-1 p-6 md:p-10">
          {current ? (
            <article className="mx-auto max-w-3xl">
              <h1 className="text-2xl font-bold text-gray-900">{current.title}</h1>
              <div className="mt-5 whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
                {current.content || 'This lesson has no content yet.'}
              </div>
            </article>
          ) : (
            <p className="text-sm text-gray-400">Pick a lesson to start.</p>
          )}
          <div className="mt-12 flex justify-center">
            <BrandLogo size="sm" />
          </div>
        </div>
      </div>
    </div>
  );
}
